export interface EventLocalDateTime {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  hasTime: boolean;
}

const EVENT_DATE_TIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,7})?)?)?(?:Z|[+-]\d{2}:?\d{2})?$/;

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function civilDayNumber(value: EventLocalDateTime): number {
  const adjustedYear = value.year - (value.month <= 2 ? 1 : 0);
  const era = Math.floor(adjustedYear / 400);
  const yearOfEra = adjustedYear - era * 400;
  const adjustedMonth = value.month + (value.month > 2 ? -3 : 9);
  const dayOfYear = Math.floor((153 * adjustedMonth + 2) / 5) + value.day - 1;
  const dayOfEra = yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfYear;
  return era * 146097 + dayOfEra;
}

/**
 * Read an event's wall-clock fields without applying a browser or suffix time zone.
 * Suffixes are accepted only for legacy API values whose fields were stored as UTC.
 */
export function parseEventLocalDateTime(value?: string | null): EventLocalDateTime | null {
  if (!value || typeof value !== 'string') return null;

  const match = EVENT_DATE_TIME_PATTERN.exec(value.trim());
  if (!match) return null;

  const [, yearText, monthText, dayText, hourText, minuteText, secondText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText ?? 0);
  const minute = Number(minuteText ?? 0);
  const second = Number(secondText ?? 0);

  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth(year, month) ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    return null;
  }

  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
    hasTime: hourText !== undefined,
  };
}

export interface FormatEventLocalDateTimeOptions {
  locale?: string | string[];
  dateStyle?: 'short' | 'medium' | 'long' | 'full';
  includeTime?: boolean;
}

/**
 * Zoned midnight is the legacy sentinel used when extraction found no time.
 * Offset-free midnight is explicit source data and remains a known time.
 */
export function hasKnownEventTime(value?: string | null): boolean {
  const parsed = parseEventLocalDateTime(value);
  if (!parsed || !parsed.hasTime) return false;
  if (parsed.hour !== 0 || parsed.minute !== 0 || parsed.second !== 0) return true;
  return !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value!.trim());
}

/** Return an offset-free ISO value suitable for URLs, APIs, and structured data. */
export function toEventLocalISOString(value?: string | null): string {
  const parsed = parseEventLocalDateTime(value);
  if (!parsed) return '';

  const date = `${parsed.year}-${pad(parsed.month)}-${pad(parsed.day)}`;
  if (!hasKnownEventTime(value)) return date;
  return `${date}T${pad(parsed.hour)}:${pad(parsed.minute)}:${pad(parsed.second)}`;
}

/** A sortable wall-clock key. No timezone is applied to the event fields. */
export function getEventLocalSortKey(value?: string | null): number | null {
  const parsed = parseEventLocalDateTime(value);
  if (!parsed) return null;
  return (
    parsed.year * 10_000_000_000 +
    parsed.month * 100_000_000 +
    parsed.day * 1_000_000 +
    parsed.hour * 10_000 +
    parsed.minute * 100 +
    parsed.second
  );
}

/** Compare only calendar dates, which is safe when the event's zone is unknown. */
export function compareEventLocalDates(first?: string | null, second?: string | null): number | null {
  const firstDate = parseEventLocalDateTime(first);
  const secondDate = parseEventLocalDateTime(second);
  if (!firstDate || !secondDate) return null;
  return Math.sign(civilDayNumber(firstDate) - civilDayNumber(secondDate));
}

/** Format a browser-local calendar date without serializing it through UTC. */
export function formatCalendarDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function localCalendarDateOffset(dayOffset: number, from: Date = new Date()): string {
  const date = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  date.setDate(date.getDate() + dayOffset);
  return formatCalendarDate(date);
}

/** Calculate duration from wall-clock fields without applying a time zone. */
export function getEventLocalDurationHours(
  startValue?: string | null,
  endValue?: string | null,
): number | null {
  const start = parseEventLocalDateTime(startValue);
  const end = parseEventLocalDateTime(endValue);
  if (!start || !end) return null;

  const dayDifference = civilDayNumber(end) - civilDayNumber(start);
  const bothTimesKnown = hasKnownEventTime(startValue) && hasKnownEventTime(endValue);
  if (!bothTimesKnown) return dayDifference > 0 ? dayDifference * 24 : null;

  const startSeconds = start.hour * 3600 + start.minute * 60 + start.second;
  const endSeconds = end.hour * 3600 + end.minute * 60 + end.second;
  const durationHours = (dayDifference * 86400 + endSeconds - startSeconds) / 3600;
  return durationHours > 0 ? durationHours : null;
}

/** Calculate duration from wall-clock fields without converting either event value. */
export function formatEventLocalDuration(startValue?: string | null, endValue?: string | null): string {
  const start = parseEventLocalDateTime(startValue);
  const end = parseEventLocalDateTime(endValue);
  if (!start || !end) return '';

  const dayDifference = civilDayNumber(end) - civilDayNumber(start);
  const bothTimesKnown = hasKnownEventTime(startValue) && hasKnownEventTime(endValue);
  if (!bothTimesKnown) {
    if (dayDifference <= 0) return '';
    return `${dayDifference} day${dayDifference === 1 ? '' : 's'}`;
  }

  const startSeconds = start.hour * 3600 + start.minute * 60 + start.second;
  const endSeconds = end.hour * 3600 + end.minute * 60 + end.second;
  const totalMinutes = Math.round((dayDifference * 86400 + endSeconds - startSeconds) / 60);
  if (totalMinutes <= 0) return '';
  if (totalMinutes < 60) return `${totalMinutes} minute${totalMinutes === 1 ? '' : 's'}`;
  if (totalMinutes % 60 === 0) {
    const hours = totalMinutes / 60;
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours} hr ${minutes} min`;
}

/** Format an event date/time while keeping its source wall-clock fields unchanged. */
export function formatEventLocalDateTime(
  value?: string | null,
  options: FormatEventLocalDateTimeOptions = {},
): string {
  const parsed = parseEventLocalDateTime(value);
  if (!parsed) return '';

  const locale = options.locale;
  const dateValue = new Date(parsed.year, parsed.month - 1, parsed.day, 12);
  const dateText = new Intl.DateTimeFormat(locale, {
    dateStyle: options.dateStyle ?? 'medium',
  }).format(dateValue);

  if (options.includeTime === false || !hasKnownEventTime(value)) return dateText;

  const timeValue = new Date(2000, 0, 15, parsed.hour, parsed.minute, parsed.second);
  const timeText = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(timeValue);
  return `${dateText} at ${timeText}`;
}